import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { useSwipeable } from "react-swipeable";
import "swiper/css";

export const Gallery = () => {
  const [galleryData, setGalleryData] = useState({});
  const [activeCategory, setActiveCategory] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [loadedImages, setLoadedImages] = useState({}); // track loaded images

  const safePath = (path) =>
    path.split("/").map((p) => encodeURIComponent(p.trim())).join("/");

  useEffect(() => {
    fetch("/images/Gallery/gallery.json")
      .then((res) => res.json())
      .then((data) => {
        const formatted = {};
        Object.keys(data).forEach((cat) => {
          formatted[cat] = data[cat].map((img) => ({
            src: safePath(`/images/Gallery/${cat}/${img}`),
          }));
        });
        setGalleryData(formatted);
        setActiveCategory(Object.keys(formatted)[0] || "");
      });
  }, []);

  const categories = Object.keys(galleryData);
  const items = galleryData[activeCategory] || [];

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setFade(true);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const showImage = (index) => {
    setFade(false);
    setTimeout(() => {
      setCurrentIndex(index);
      setFade(true);
    }, 150);
  };

  const prev = () => showImage(currentIndex === 0 ? items.length - 1 : currentIndex - 1);
  const next = () => showImage(currentIndex === items.length - 1 ? 0 : currentIndex + 1);

  // keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxOpen, currentIndex, items]);

  // swipe support
  const handlers = useSwipeable({
    onSwipedLeft: () => next(),
    onSwipedRight: () => prev(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  // Preload next and previous images
  const preloadImage = (src) => {
    const img = new Image();
    img.src = src;
  };

  useEffect(() => {
    if (!items.length) return;
    const nextIndex = currentIndex === items.length - 1 ? 0 : currentIndex + 1;
    const prevIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;

    preloadImage(items[nextIndex].src);
    preloadImage(items[prevIndex].src);
  }, [currentIndex, items]);

  return (
    <section id="gallery" className="py-20 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* ---------- Section Header ---------- */}
        <div className="text-center max-w-3xl mx-auto mb-8">
          <div className="inline-block bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-300 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Gallery
          </div>
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {activeCategory || "Gallery"}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Explore moments captured from our events, experiences, and daily life. Click any image to view it in full size and navigate the gallery easily.
          </p>
        </div>

        {/* ---------- Categories ---------- */}
        <div className="flex justify-center gap-3 mb-8 flex-wrap">
          {categories.map((cat) => (
            <div key={cat} className="relative">
              <button
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-2 rounded-full text-base font-semibold capitalize transition-all duration-300 transform hover:scale-105 ${
                  activeCategory === cat
                    ? "bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg"
                    : "bg-gray-200 dark:bg-gray-800 dark:text-gray-300 hover:bg-emerald-200 dark:hover:bg-emerald-700"
                }`}
              >
                {cat}
              </button>
              <span
                className={`absolute -bottom-1 left-0 w-full h-1 bg-emerald-500 rounded-full transition-all duration-300 ${
                  activeCategory === cat ? "scale-x-100" : "scale-x-0"
                }`}
              ></span>
            </div>
          ))}
        </div>

        {/* ---------- Continuous Auto-Scroll Gallery ---------- */}
        <Swiper
          key={activeCategory}
          modules={[Autoplay]}
          spaceBetween={10}
          slidesPerView={4}
          loop={true}
          speed={20000}
          autoplay={{ delay: 0, disableOnInteraction: false, pauseOnMouseEnter: true }}
          breakpoints={{ 640: { slidesPerView: 2 }, 768: { slidesPerView: 3 }, 1024: { slidesPerView: 4 } }}
        >
          {[...items, ...items].map((item, index) => (
            <SwiperSlide key={index}>
              <img
                src={item.src}
                alt={`Image ${index + 1}`}
                loading="lazy"
                onLoad={() => setLoadedImages((prev) => ({ ...prev, [item.src]: true }))}
                className={`w-full h-48 object-cover rounded-lg cursor-pointer shadow-md transition-transform duration-500 ${
                  loadedImages[item.src] ? "opacity-100" : "opacity-0"
                } hover:scale-105`}
                onClick={() => openLightbox(index % items.length)}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* ---------- Lightbox ---------- */}
        {lightboxOpen && items.length > 0 && (
          <div
            {...handlers}
            className={`fixed inset-0 flex flex-col items-center justify-center z-50 p-4 transition-all duration-500 ${
              fade ? "bg-black/90" : "bg-black/70"
            }`}
            onClick={closeLightbox}
          >
            {/* Previous Button */}
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center w-16 h-16 text-5xl md:text-6xl bg-black/30 hover:bg-black/60 text-white rounded-full transition-all duration-300 shadow-lg"
            >
              ‹
            </button>

            {/* Main Image + Counter + Thumbnails */}
            <div className="flex flex-col items-center mb-4">
              <img
                src={items[currentIndex].src}
                alt={`Image ${currentIndex + 1}`}
                loading="lazy"
                onLoad={() =>
                  setLoadedImages((prev) => ({ ...prev, [items[currentIndex].src]: true }))
                }
                className={`max-h-[70vh] max-w-[90vw] rounded-lg mb-4 transition-all duration-500 transform ${
                  fade ? "opacity-100 scale-100" : "opacity-0 scale-95"
                } ${loadedImages[items[currentIndex].src] ? "opacity-100" : "opacity-0"}`}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white bg-black/50 px-3 py-1 rounded-full text-sm">
                Image {currentIndex + 1} of {items.length}
              </div>

              {/* Thumbnail Strip */}
              <div
                className={`flex overflow-x-auto gap-2 py-2 w-full max-w-[90vw] mt-2 transform transition-all duration-500 ${
                  lightboxOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                {items.map((item, index) => (
                  <img
                    key={index}
                    src={item.src}
                    alt={`Thumbnail ${index + 1}`}
                    loading="lazy"
                    onLoad={() =>
                      setLoadedImages((prev) => ({ ...prev, [item.src]: true }))
                    }
                    className={`w-16 h-16 object-cover rounded cursor-pointer border-2 transition-all duration-500 transform hover:scale-110 ${
                      currentIndex === index ? "border-emerald-500" : "border-transparent"
                    } ${loadedImages[item.src] ? "opacity-100" : "opacity-0"}`}
                    onClick={() => showImage(index)}
                  />
                ))}
              </div>
            </div>

            {/* Next Button */}
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center w-16 h-16 text-5xl md:text-6xl bg-black/30 hover:bg-black/60 text-white rounded-full transition-all duration-300 shadow-lg"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
